import { useMemo } from 'react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Card, CardBody, CardHeader } from './ui/Card';
import { KpiCard } from './ui/KpiCard';
import { Skeleton } from './ui/States';
import { buildFormatters } from '../lib/format';
import type { Host } from '../lib/host';

/**
 * Payload shape — matches the response of `get_brand_stats`.
 */
export type BrandStatsData = {
  brand_id: string;
  brand_name: string;
  from?: string;
  to?: string;
  visibility_score: number;
  mention_count: number;
  mention_coverage: number;
  avg_position: number;
  sentiment_score: number;
  trend: {
    score_change: number;
    mention_change: number;
    position_change: number;
  };
  history: Array<{
    date: string;
    visibility_score: number;
    mention_count: number;
  }>;
};

export function BrandStats({ data, host }: { data: BrandStatsData; host: Host }) {
  const fmt = useMemo(() => buildFormatters(host.locale), [host.locale]);
  const subtitle = `${data.brand_name} · ${fmt.range(data.from, data.to)}`;

  if (!data.history?.length && data.mention_count === 0) {
    return (
      <Card>
        <CardHeader title="Brand visibility" subtitle={subtitle} />
        <CardBody>
          <div className="text-sm text-secondary leading-relaxed">
            No visibility data for {data.brand_name} in this window. Try a broader date range or confirm the brand is
            tracked.
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader title="Brand visibility" subtitle={subtitle} />
      <CardBody className="space-y-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" role="list" aria-label="Key visibility metrics">
          <KpiCard
            label="Visibility"
            value={formatScore(data.visibility_score)}
            delta={data.trend.score_change}
            goodDirection="up"
            hint="0–1, higher is better"
          />
          <KpiCard
            label="Mentions"
            value={fmt.number(data.mention_count)}
            delta={data.trend.mention_change}
            goodDirection="up"
          />
          <KpiCard
            label="Avg position"
            value={data.avg_position.toFixed(1)}
            delta={data.trend.position_change}
            goodDirection="down"
            hint="lower is better"
          />
          <KpiCard
            label="Sentiment"
            value={formatSentiment(data.sentiment_score)}
            hint={`Coverage ${(data.mention_coverage * 100).toFixed(0)}%`}
          />
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-wider text-muted font-medium mb-2">Daily visibility</div>
          <div
            className="h-44 w-full"
            role="img"
            aria-label={`Daily visibility area chart over ${data.history.length} days.`}
          >
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.history} margin={{ top: 4, right: 6, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="visibilityArea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--color-accent)" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="var(--color-accent)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="2 4" vertical={false} stroke="var(--chart-grid)" />
                <XAxis
                  dataKey="date"
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={fmt.shortDate}
                  tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  domain={[0, 'auto']}
                  width={36}
                  tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
                />
                <Tooltip
                  cursor={{ stroke: 'var(--color-border-light)', strokeWidth: 1 }}
                  contentStyle={{
                    background: 'var(--color-surface)',
                    border: '1px solid var(--color-border-light)',
                    borderRadius: '0.5rem',
                    fontSize: '12px',
                    color: 'var(--color-text-primary)',
                  }}
                  labelFormatter={(label: string) => fmt.date(label)}
                  formatter={(value: number) => [formatScore(value), 'visibility']}
                />
                <Area
                  type="monotone"
                  dataKey="visibility_score"
                  stroke="var(--color-accent)"
                  strokeWidth={2}
                  fill="url(#visibilityArea)"
                  isAnimationActive={prefersReducedMotion() ? false : true}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

function formatScore(n: number): string {
  return n.toFixed(2);
}

function formatSentiment(n: number): string {
  if (n === 0) return 'neutral';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(2)}`;
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export const BrandStatsSkeleton = () => <Skeleton shape="brand-stats" />;
